// Export utility functions for PDF, SVG, and DXF formats

export const handleExportPDF = async (optimizationResults, projectName, kerfThickness, userSubscription) => {
  try {
    // Check if we have optimization results (new structure uses stock_sheets)
    if (!optimizationResults || !optimizationResults.stock_sheets || optimizationResults.stock_sheets.length === 0) {
      alert('Please run the optimization first before exporting to PDF.');
      return;
    }

    // Dynamic import of jspdf and html2canvas
    const jsPDF = (await import('jspdf')).default;
    const html2canvas = (await import('html2canvas')).default;
    
    const pdf = new jsPDF('portrait', 'in', 'letter'); // 8.5x11 inches (portrait)
    const pageWidth = 8.5;
    const pageHeight = 11;
    const margin = 0.5;
    const contentWidth = pageWidth - (2 * margin);
    
    // Format date
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const title = projectName || 'Untitled Project';
    
    // Helper function to add header to every page
    const addPageHeader = () => {
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Cut Creator', margin, 0.4);
      
      // Add date (right-aligned)
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(currentDate, pageWidth - margin, 0.4, { align: 'right' });
      
      // Draw horizontal line under header
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.01);
      pdf.line(margin, 0.5, pageWidth - margin, 0.5);
    };
    
    // Add header to first page
    addPageHeader();
    
    // Calculate summary statistics (using new stock_sheets structure)
    const sheetBreakdown = {};
    optimizationResults.stock_sheets?.forEach(stockSheet => {
      const sheetSize = stockSheet.statistics?.sheet_size || {};
      const width = sheetSize.width || 0;
      const length = sheetSize.length || 0;
      const materialType = stockSheet.statistics?.materialType || 'Unknown';
      const quantity = stockSheet.statistics?.quantity || 1;
      const key = `${width}x${length}-${materialType}`;
      if (!sheetBreakdown[key]) {
        sheetBreakdown[key] = {
          size: `${width}" × ${length}"`,
          material: materialType,
          quantity: 0
        };
      }
      sheetBreakdown[key].quantity += quantity;
    });
    
    const totalArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
      const sheetSize = stockSheet.statistics?.sheet_size || {};
      const width = sheetSize.width || 0;
      const length = sheetSize.length || 0;
      const quantity = stockSheet.statistics?.quantity || 1;
      const sheetArea = width * length * quantity;
      return sum + sheetArea;
    }, 0) || 0;
    
    const usedArea = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
      const panelArea = stockSheet.panels?.reduce((pSum, p) => pSum + (p.width * p.length), 0) || 0;
      const quantity = stockSheet.statistics?.quantity || 1;
      return sum + (panelArea * quantity);
    }, 0) || 0;
    
    const wastedArea = totalArea - usedArea;
    const usedPercentage = totalArea > 0 ? ((usedArea / totalArea) * 100).toFixed(1) : '0';
    const wastedPercentage = totalArea > 0 ? ((wastedArea / totalArea) * 100).toFixed(1) : '0';
    
    const totalCuts = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
      const quantity = stockSheet.statistics?.quantity || 1;
      return sum + ((stockSheet.cutlist?.length || 0) * quantity);
    }, 0) || 0;
    
    const totalLength = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => {
      const cutLength = stockSheet.cutlist?.reduce((cSum, cut) => {
        return cSum + (cut.cut_length || 0);
      }, 0) || 0;
      const quantity = stockSheet.statistics?.quantity || 1;
      return sum + (cutLength * quantity);
    }, 0) || 0;
    
    // Draw compact summary table (similar to image style)
    let currentY = 0.7; // Start below header
    let summaryTableYStart = currentY;
    
    // Row configuration
    const rowHeight = 0.15;
    const labelColWidth = 1.5;
    const valueColWidth = 1.0;
    const centerColWidth = 1.5;
    const cellPadding = 0.05;
    
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.005);
    pdf.setFontSize(8);
    
    // Left column - Stock and Area info
    const leftColX = margin;
    let leftY = summaryTableYStart;
    
    // Used stock sheets
    pdf.setFont(undefined, 'bold');
    pdf.text('Used stock sheets', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    const totalSheets = optimizationResults.stock_sheets?.reduce((sum, stockSheet) => sum + (stockSheet.statistics?.quantity || 1), 0) || 0;
    pdf.text(totalSheets.toString(), leftColX + labelColWidth, leftY);
    leftY += rowHeight;
    
    // Total used area
    pdf.setFont(undefined, 'bold');
    pdf.text('Total used area', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    pdf.text(`${usedArea.toFixed(0)} (${usedPercentage}%)`, leftColX + labelColWidth, leftY);
    leftY += rowHeight;
    
    // Total wasted area
    pdf.setFont(undefined, 'bold');
    pdf.text('Total wasted area', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    pdf.text(`${wastedArea.toFixed(0)} (${wastedPercentage}%)`, leftColX + labelColWidth, leftY);
    leftY += rowHeight;
    
    // Total cuts
    pdf.setFont(undefined, 'bold');
    pdf.text('Total cuts', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    pdf.text(totalCuts.toString(), leftColX + labelColWidth, leftY);
    leftY += rowHeight;
    
    // Total cut length
    pdf.setFont(undefined, 'bold');
    pdf.text('Total cut length', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    pdf.text(`${totalLength.toFixed(2)}"`, leftColX + labelColWidth, leftY);
    leftY += rowHeight;
    
    // Kerf thickness
    pdf.setFont(undefined, 'bold');
    pdf.text('Cut blade / kerf thickness', leftColX, leftY);
    pdf.setFont(undefined, 'normal');
    pdf.text(`${kerfThickness}"`, leftColX + labelColWidth, leftY);
    
    // Center column - Stock sheets and Panels
    const centerColX = leftColX + labelColWidth + valueColWidth + 0.2;
    let centerY = summaryTableYStart;
    
    // Stock sheets header
    pdf.setFont(undefined, 'bold');
    pdf.text('Stock sheets', centerColX, centerY);
    centerY += rowHeight;
    
    // List each stock sheet type
    pdf.setFont(undefined, 'normal');
    const sheets = Object.values(sheetBreakdown);
    sheets.forEach(sheet => {
      pdf.text(`${sheet.size} (${sheet.material}) x ${sheet.quantity}`, centerColX + 0.1, centerY);
      centerY += rowHeight;
    });
    
    // Panels header (if we have space)
    centerY += 0.1; // Small gap
    pdf.setFont(undefined, 'bold');
    pdf.text('Panels', centerColX, centerY);
    centerY += rowHeight;
    
    // Panel count
    pdf.setFont(undefined, 'normal');
    pdf.text(`Total panels: ${optimizationResults.totalPanelsCut || 0}`, centerColX + 0.1, centerY);
    centerY += rowHeight;
    
    // Right column - unplaced panels
    const rightColX = centerColX + centerColWidth + 0.2;
    let rightY = summaryTableYStart;

    // Add unplaced panels if any (in right column, below panels)
    if (optimizationResults.unplacedPanels && optimizationResults.unplacedPanels.length > 0) {
      pdf.setTextColor(255, 0, 0); // Red for warning
      pdf.setFont(undefined, 'bold');
      pdf.text('Unplaced Panels', rightColX, rightY);
      rightY += rowHeight;
      
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(255, 0, 0); // Keep red for list items
      optimizationResults.unplacedPanels.forEach(panel => {
        const panelDim = `${panel.label} (${panel.width} × ${panel.length})`;
        pdf.text(panelDim, rightColX + 0.1, rightY);
        rightY += rowHeight;
      });
      
      pdf.setTextColor(0, 0, 0); // Reset color
    }

    currentY = Math.max(leftY, centerY, rightY) + 0.1; // Update current Y position

    // Draw horizontal line under summary table
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.01);
    pdf.line(margin, currentY, pageWidth - margin, currentY);

    currentY += 0.2; // Small gap before layouts
    
    // Use backend's grouping - sheets are already grouped with quantity field
    const layoutGroups = [];
    
    optimizationResults.stock_sheets.forEach((stockSheet, index) => {
      // Skip empty stock sheets (no panels)
      if (!stockSheet.panels || stockSheet.panels.length === 0) {
        return;
      }
      
      // Backend already groups identical sheets, so use the quantity from statistics
      const quantity = stockSheet.statistics?.quantity || 1;
      const sheetIndices = stockSheet.statistics?.sheet_indices || [index + 1];
      
      layoutGroups.push({
        stockSheet,
        firstIndex: sheetIndices[0] - 1, // Convert to 0-based index
        count: quantity,
        indices: sheetIndices
      });
    });

    // Process first visualizer separately to place it in teal box on first page
    let firstVisProcessed = false;
    if (layoutGroups.length > 0) {
      const firstGroup = layoutGroups[0];
      const stockSheet = firstGroup.stockSheet;
      // Use the first sheet index from the grouped sheet's indices (0-based for data-sheet-index)
      const sheetIndices = stockSheet.statistics?.sheet_indices || [1];
      const i = sheetIndices[0] - 1; // Convert to 0-based index for data-sheet-index
      
      // Find the visualization div for this sheet
      const vizElement = document.querySelector(`[data-sheet-index="${i}"]`);
      if (vizElement) {
        // Extract the clean SVG for PDF (without the extra 8 units for hover dimensions)
        const svgElement = vizElement.querySelector('svg[data-for-pdf="true"]');
        if (svgElement) {
          // Clone the SVG
          const clonedSvg = svgElement.cloneNode(true);
          
          // Get the SVG dimensions from viewBox
          const viewBox = clonedSvg.getAttribute('viewBox');
          const viewBoxValues = viewBox ? viewBox.split(' ').map(Number) : [0, 0, 100, 100];
          const svgWidth = viewBoxValues[2];
          const svgHeight = viewBoxValues[3];
          
          console.log('SVG dimensions from viewBox:', { svgWidth, svgHeight });
          
          // The hidden SVG might already be rotated by the React component
          // Don't apply additional rotation logic - just fit it to the constraints
          const aspectRatio = svgHeight / svgWidth;
          
          console.log('Aspect ratio:', aspectRatio);
          
          // Position below the horizontal line on first page
          // Use currentY (which is already positioned after the line) as the top position
          const diagramLeft = margin;
          const diagramTop = currentY + 0.0; // Small gap after the line
          
          // For non-rotated diagrams: use 2/3 of horizontal space
          // For rotated diagrams: use full available width
          const useTwoThirds = aspectRatio <= 1; // aspectRatio > 1 means tall/rotated
          const maxDiagramWidth = useTwoThirds 
            ? (pageWidth - 2 * margin) * 2 / 3 
            : (pageWidth - 2 * margin); // Full width for rotated diagrams
          const maxDiagramHeight = pageHeight - diagramTop - margin; // Remaining vertical space
          
          console.log('Max diagram constraints (inches):', { maxDiagramWidth, maxDiagramHeight });
          
          // Calculate the rendering size based on constraints while maintaining aspect ratio
          // Start with a reasonable base size and adjust based on PDF constraints
          const maxRenderWidth = maxDiagramWidth * 96;  // Convert inches to pixels (96 DPI)
          const maxRenderHeight = maxDiagramHeight * 96; // Convert inches to pixels (96 DPI)
          
          let renderWidth, renderHeight;
          
          // If the aspect ratio suggests it's tall (height > width), prioritize height constraint
          if (aspectRatio > 1) {
            // Tall diagram - fill the available height first
            renderHeight = maxRenderHeight;
            renderWidth = renderHeight / aspectRatio;
            
            // If width exceeds max width, constrain by width instead
            if (renderWidth > maxRenderWidth) {
              renderWidth = maxRenderWidth;
              renderHeight = renderWidth * aspectRatio;
            }
          } else {
            // Wide diagram - fill the available width first (2/3 of page)
            renderWidth = maxRenderWidth;
            renderHeight = renderWidth * aspectRatio;
            
            // If height exceeds max height, constrain by height instead
            if (renderHeight > maxRenderHeight) {
              renderHeight = maxRenderHeight;
              renderWidth = renderHeight / aspectRatio;
            }
          }
          
          console.log('Final render dimensions (pixels):', { renderWidth, renderHeight });
          console.log('Final PDF dimensions (inches):', { pdfWidth: renderWidth / 96, pdfHeight: renderHeight / 96 });
          
          // Create a container for the SVG
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'relative';
          tempContainer.style.display = 'inline-block';
          tempContainer.style.backgroundColor = '#ffffff';
          tempContainer.style.overflow = 'visible';
          
          clonedSvg.style.width = `${renderWidth}px`;
          clonedSvg.style.height = `${renderHeight}px`;
          tempContainer.style.width = `${renderWidth}px`;
          tempContainer.style.height = `${renderHeight}px`;
          
          tempContainer.appendChild(clonedSvg);
          
          // Add to document temporarily (off-screen)
          tempContainer.style.position = 'absolute';
          tempContainer.style.left = '-9999px';
          document.body.appendChild(tempContainer);
          
          // Capture just the SVG
          const canvas = await html2canvas(tempContainer, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
          });
          
          document.body.removeChild(tempContainer);
          
          const imgData = canvas.toDataURL('image/png');
          
          // Use the render dimensions we calculated, converting back from pixels to inches (96 DPI)
          const pdfWidth = renderWidth / 96;
          const pdfHeight = renderHeight / 96;
          
          // Position the diagram on the left side below the summary table
          const xPosition = diagramLeft;
          const yPosition = diagramTop;
          
          pdf.addImage(imgData, 'PNG', xPosition, yPosition, pdfWidth, pdfHeight);
          
          // Add sheet statistics table to the right of the diagram
          const tableX = xPosition + pdfWidth + 0.2; // 0.2" gap after diagram
          const tableY = yPosition; // Align with top of diagram
          
          // Get stock sheet information for this sheet
          const stockSheet = optimizationResults.stock_sheets[0]; // First stock sheet since we're on first page
          if (stockSheet) {
            // Add sheet title above the table
            const sheetQuantity = stockSheet.statistics?.quantity || 1;
            const sheetIndices = stockSheet.statistics?.sheet_indices || [1];
            let sheetTitle;
            if (sheetQuantity > 1 && sheetIndices.length > 1) {
              sheetTitle = `Sheet #${sheetIndices[0]}-#${sheetIndices[sheetIndices.length - 1]}`;
            } else {
              sheetTitle = `Sheet #${sheetIndices[0] || 1}`;
            }
            
            pdf.setFont(undefined, 'bold');
            pdf.setFontSize(12);
            pdf.text(sheetTitle, tableX, tableY + 0.1);
            
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.005);
            pdf.setFontSize(8);
            
            const tableRowHeight = 0.15;
            const labelColWidth = 1.2;
            let tableRowY = tableY + 0.4;
            
            // Calculate sheet statistics
            const sheetSizeObj = stockSheet.statistics?.sheet_size || {};
            const sheetWidth = sheetSizeObj.width || 0;
            const sheetLength = sheetSizeObj.length || 0;
            const sheetArea = sheetWidth * sheetLength;
            const panelArea = stockSheet.panels?.reduce((sum, panel) => sum + (panel.width * panel.length), 0) || 0;
            const wastedArea = sheetArea - panelArea;
            const usedPercentage = sheetArea > 0 ? ((panelArea / sheetArea) * 100).toFixed(1) : '0';
            const wastedPercentage = sheetArea > 0 ? ((wastedArea / sheetArea) * 100).toFixed(1) : '0';
            const panelCount = stockSheet.panels?.length || 0;
            const wastePanelCount = stockSheet.waste_panels ? stockSheet.waste_panels.length : 0;
            
            const sheetSize = `${sheetWidth || 'N/A'}" × ${sheetLength || 'N/A'}"`;
            const material = stockSheet.statistics?.materialType || 'N/A';
            const quantity = stockSheet.statistics?.quantity || 1;
            
            // Create array of [label, value] pairs
            const stats = [
              ['Sheet Size', sheetSize],
              ['Material Type', material],
              ['Quantity', quantity.toString()],
              ['Used Area %', `${usedPercentage}%`],
              ['Wasted Area %', `${wastedPercentage}%`],
              ['Panels', panelCount.toString()],
              ['Waste Panels', wastePanelCount.toString()]
            ];
            
            // Draw labels in left column, values in right column
            const labelColX = tableX;
            const pageRightMargin = pageWidth - margin;
            const valueColX = pageRightMargin; // Right-justify with some padding
            
            for (const [label, value] of stats) {
              pdf.setFont(undefined, 'bold');
              pdf.text(label, labelColX, tableRowY);
              
              pdf.setFont(undefined, 'normal');
              pdf.text(value, valueColX, tableRowY, { align: 'right' });
              
              tableRowY += tableRowHeight;
            }

            // Draw horizontal line under statistics table
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.01);
            pdf.line(tableX, tableRowY, pageWidth - margin, tableRowY);
            
            // Add cutting sequence table below the line
            tableRowY += 0.2; // Gap after line
            
            const cuttingSequence = stockSheet.cutlist || [];
            const cutTableRowHeight = 0.12;
            
            // Calculate available width for the table (from tableX to page margin)
            const tableAvailableWidth = (pageWidth - margin) - tableX;
            const cutTableColWidths = [0.3, 0.6, tableAvailableWidth - 0.9]; // Step, Cut, Result (fills remaining space)
            const totalTableWidth = cutTableColWidths[0] + cutTableColWidths[1] + cutTableColWidths[2];
            
            // Draw header row
            pdf.setFillColor(240, 240, 240);
            pdf.setDrawColor(150, 150, 150);
            pdf.setLineWidth(0.005);
            pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight, 'FD');
            
            pdf.setFont(undefined, 'bold');
            pdf.setFontSize(8);
            pdf.text('#', tableX + cutTableColWidths[0] / 2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'center' });
            pdf.text('Cut', tableX + cutTableColWidths[0] + 0.2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });
            pdf.text('Result', tableX + cutTableColWidths[0] + cutTableColWidths[1] + 0.3, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });

            tableRowY += cutTableRowHeight;
            
            // Draw all data rows
            pdf.setFont(undefined, 'normal');
            
            for (let i = 0; i < cuttingSequence.length; i++) {
              const cut = cuttingSequence[i];
              if (!cut) continue;
              
              // Use cut_type from new structure, fallback to type
              const cutType = cut.cut_type || cut.type || 'vertical';
              const cutDirection = cutType === 'horizontal' ? 'H' : 'V';
              // Use relative_position from new structure, fallback to relativePosition
              const relativePos = cut.relative_position || cut.relativePosition;
              const cutLength = relativePos ? relativePos.toFixed(2) : '0.00';
              const cutLabel = `${cutDirection} ${cutLength}"`;
              
              // Extract panel name if this cut results in a finished panel (using new results structure)
              let resultLabel = '';
              if (cut.results && Array.isArray(cut.results)) {
                // Find the first result that is a panel
                const panelResult = cut.results.find(r => r.type === 'panel');
                if (panelResult && panelResult.id) {
                  // Find the panel by ID
                  const panel = stockSheet.panels?.find(p => p.id === panelResult.id);
                  if (panel) {
                    // Return label if available, otherwise use panel ID
                    resultLabel = panel.label || `Panel ${panel.id}`;
                  }
                }
              }
              // Fallback to old creates field if results not available
              if (!resultLabel && cut.creates && cut.creates.includes('Finished panel')) {
                const match = cut.creates.match(/Finished panel '([^']+)'/);
                if (match) {
                  resultLabel = match[1];
                }
              }
              
              // Draw row with alternating background
              if (i % 2 === 0) {
                pdf.setFillColor(250, 250, 250);
              } else {
                pdf.setFillColor(245, 245, 245);
              }
              pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight, 'F');
              
              pdf.setDrawColor(200, 200, 200);
              pdf.setLineWidth(0.003);
              pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight);
              
              pdf.text((i + 1).toString(), tableX + cutTableColWidths[0] / 2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'center' });
              pdf.text(cutLabel, tableX + cutTableColWidths[0] + 0.2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });
              pdf.text(resultLabel, tableX + cutTableColWidths[0] + cutTableColWidths[1] + 0.3, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });
              
              tableRowY += cutTableRowHeight;
            }

          }
          
          firstVisProcessed = true;
        }
      }
    }
    
    // Process remaining layout groups (skip first if already processed)
    for (let groupIndex = 0; groupIndex < layoutGroups.length; groupIndex++) {
      // Skip first group if it was already processed for teal box placement
      if (firstVisProcessed && groupIndex === 0) {
        continue;
      }
      
      const group = layoutGroups[groupIndex];
      const stockSheet = group.stockSheet;
      // Use the first sheet index from the grouped sheet's indices (0-based for data-sheet-index)
      const sheetIndices = stockSheet.statistics?.sheet_indices || [groupIndex + 1];
      const i = sheetIndices[0] - 1; // Convert to 0-based index for data-sheet-index
      
      // Add new page for each sheet
      pdf.addPage();
      addPageHeader(); // Add header to each layout page
      
      // Position for this page's content (similar to first page but without summary table)
      let pageContentY = 0.7; // Start below header
      
      // Find the visualization div for this sheet
      const vizElement = document.querySelector(`[data-sheet-index="${i}"]`);
      if (vizElement) {
        // Extract the clean SVG for PDF (without the extra 8 units for hover dimensions)
        const svgElement = vizElement.querySelector('svg[data-for-pdf="true"]');
        if (svgElement) {
          // Clone the SVG
          const clonedSvg = svgElement.cloneNode(true);
          
          // Get the SVG dimensions from viewBox
          const viewBox = clonedSvg.getAttribute('viewBox');
          const viewBoxValues = viewBox ? viewBox.split(' ').map(Number) : [0, 0, 100, 100];
          const svgWidth = viewBoxValues[2];
          const svgHeight = viewBoxValues[3];
          
          const aspectRatio = svgHeight / svgWidth;
          
          // Position diagram on the left
          const diagramLeft = margin;
          const diagramTop = pageContentY;
          
          // Use 2/3 of horizontal space for wide diagrams, full width for rotated
          const useTwoThirds = aspectRatio <= 1;
          const maxDiagramWidth = useTwoThirds 
            ? (pageWidth - 2 * margin) * 2 / 3 
            : (pageWidth - 2 * margin);
          const maxDiagramHeight = pageHeight - diagramTop - margin;
          
          const maxRenderWidth = maxDiagramWidth * 96;
          const maxRenderHeight = maxDiagramHeight * 96;
          
          let renderWidth, renderHeight;
          
          if (aspectRatio > 1) {
            renderHeight = maxRenderHeight;
            renderWidth = renderHeight / aspectRatio;
            
            if (renderWidth > maxRenderWidth) {
              renderWidth = maxRenderWidth;
              renderHeight = renderWidth * aspectRatio;
            }
          } else {
            renderWidth = maxRenderWidth;
            renderHeight = renderWidth * aspectRatio;
            
            if (renderHeight > maxRenderHeight) {
              renderHeight = maxRenderHeight;
              renderWidth = renderHeight / aspectRatio;
            }
          }
          
          // Create a container for the SVG
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'relative';
          tempContainer.style.display = 'inline-block';
          tempContainer.style.backgroundColor = '#ffffff';
          tempContainer.style.overflow = 'visible';
          
          clonedSvg.style.width = `${renderWidth}px`;
          clonedSvg.style.height = `${renderHeight}px`;
          tempContainer.style.width = `${renderWidth}px`;
          tempContainer.style.height = `${renderHeight}px`;
          
          tempContainer.appendChild(clonedSvg);
          
          // Add to document temporarily (off-screen)
          tempContainer.style.position = 'absolute';
          tempContainer.style.left = '-9999px';
          document.body.appendChild(tempContainer);
          
          // Capture just the SVG
          const canvas = await html2canvas(tempContainer, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
          });
          
          document.body.removeChild(tempContainer);
          
          const imgData = canvas.toDataURL('image/png');
          
          const pdfWidth = renderWidth / 96;
          const pdfHeight = renderHeight / 96;
          
          // Position the diagram on the left side
          const xPosition = diagramLeft;
          const yPosition = diagramTop;
          
          pdf.addImage(imgData, 'PNG', xPosition, yPosition, pdfWidth, pdfHeight);
          
          // Add sheet statistics table to the right of the diagram
          const tableX = xPosition + pdfWidth + 0.2;
          const tableY = yPosition;
          
          if (stockSheet) {
            // Add sheet title above the table
            const sheetQuantity = stockSheet.statistics?.quantity || 1;
            const sheetIndices = stockSheet.statistics?.sheet_indices || [group.firstIndex + 1];
            let sheetTitle;
            if (sheetQuantity > 1 && sheetIndices.length > 1) {
              sheetTitle = `Sheet #${sheetIndices[0]}-#${sheetIndices[sheetIndices.length - 1]}`;
            } else {
              sheetTitle = `Sheet #${sheetIndices[0] || group.firstIndex + 1}`;
            }
            
            pdf.setFont(undefined, 'bold');
            pdf.setFontSize(12);
            pdf.text(sheetTitle, tableX, tableY + 0.15);
            
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineWidth(0.005);
            pdf.setFontSize(8);
            
            const tableRowHeight = 0.15;
            const labelColWidth = 1.2;
            let tableRowY = tableY + 0.4;
            
            // Calculate sheet statistics
            const sheetSizeObj = stockSheet.statistics?.sheet_size || {};
            const sheetWidth = sheetSizeObj.width || 0;
            const sheetLength = sheetSizeObj.length || 0;
            const sheetArea = sheetWidth * sheetLength;
            const panelArea = stockSheet.panels?.reduce((sum, panel) => sum + (panel.width * panel.length), 0) || 0;
            const wastedArea = sheetArea - panelArea;
            const usedPercentage = sheetArea > 0 ? ((panelArea / sheetArea) * 100).toFixed(1) : '0';
            const wastedPercentage = sheetArea > 0 ? ((wastedArea / sheetArea) * 100).toFixed(1) : '0';
            const panelCount = stockSheet.panels?.length || 0;
            const wastePanelCount = stockSheet.waste_panels ? stockSheet.waste_panels.length : 0;
            
            const sheetSize = `${sheetWidth || 'N/A'}" × ${sheetLength || 'N/A'}"`;
            const material = stockSheet.statistics?.materialType || 'N/A';
            const quantity = stockSheet.statistics?.quantity || 1;
            
            // Create array of [label, value] pairs
            const stats = [
              ['Sheet Size', sheetSize],
              ['Material Type', material],
              ['Quantity', quantity.toString()],
              ['Used Area %', `${usedPercentage}%`],
              ['Wasted Area %', `${wastedPercentage}%`],
              ['Panels', panelCount.toString()],
              ['Waste Panels', wastePanelCount.toString()]
            ];
            
            // Draw labels in left column, values in right column
            const labelColX = tableX;
            const pageRightMargin = pageWidth - margin;
            const valueColX = pageRightMargin;
            
            for (const [label, value] of stats) {
              pdf.setFont(undefined, 'bold');
              pdf.text(label, labelColX, tableRowY);
              
              pdf.setFont(undefined, 'normal');
              pdf.text(value, valueColX, tableRowY, { align: 'right' });
              
              tableRowY += tableRowHeight;
            }

            // Draw horizontal line under statistics table
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.01);
            pdf.line(tableX, tableRowY, pageWidth - margin, tableRowY);
            
            // Add cutting sequence table below the line
            tableRowY += 0.2;
            
            const cuttingSequence = stockSheet.cutlist || [];
            const cutTableRowHeight = 0.12;
            
            // Calculate available width for the table (from tableX to page margin)
            const tableAvailableWidth = (pageWidth - margin) - tableX;
            const cutTableColWidths = [0.3, 0.6, tableAvailableWidth - 0.9]; // Step, Cut, Result (fills remaining space)
            const totalTableWidth = cutTableColWidths[0] + cutTableColWidths[1] + cutTableColWidths[2];
            
            // Draw header row
            pdf.setFillColor(240, 240, 240);
            pdf.setDrawColor(150, 150, 150);
            pdf.setLineWidth(0.005);
            pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight, 'FD');
            
            pdf.setFont(undefined, 'bold');
            pdf.setFontSize(8);
            pdf.text('#', tableX + cutTableColWidths[0] / 2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'center' });
            pdf.text('Cut', tableX + cutTableColWidths[0] + 0.2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });
            pdf.text('Result', tableX + cutTableColWidths[0] + cutTableColWidths[1] + 0.3, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'left' });

            tableRowY += cutTableRowHeight;
            
            // Draw all data rows
            pdf.setFont(undefined, 'normal');
            
            for (let cutIdx = 0; cutIdx < cuttingSequence.length; cutIdx++) {
              const cut = cuttingSequence[cutIdx];
              if (!cut) continue;
              
              // Use cut_type from new structure, fallback to type
              const cutType = cut.cut_type || cut.type || 'vertical';
              const cutDirection = cutType === 'horizontal' ? 'H' : 'V';
              // Use relative_position from new structure, fallback to relativePosition
              const relativePos = cut.relative_position || cut.relativePosition;
              const cutLength = relativePos ? relativePos.toFixed(2) : '0.00';
              const cutLabel = `${cutDirection} ${cutLength}"`;
              
              // Extract panel name if this cut results in a finished panel (using new results structure)
              let resultLabel = '';
              if (cut.results && Array.isArray(cut.results)) {
                // Find the first result that is a panel
                const panelResult = cut.results.find(r => r.type === 'panel');
                if (panelResult && panelResult.id) {
                  // Find the panel by ID
                  const panel = stockSheet.panels?.find(p => p.id === panelResult.id);
                  if (panel) {
                    // Return label if available, otherwise use panel ID
                    resultLabel = panel.label || `Panel ${panel.id}`;
                  }
                }
              }
              // Fallback to old creates field if results not available
              if (!resultLabel && cut.creates && cut.creates.includes('Finished panel')) {
                const match = cut.creates.match(/Finished panel '([^']+)'/);
                if (match) {
                  resultLabel = match[1];
                }
              }
              
              // Draw row with alternating background
              if (cutIdx % 2 === 0) {
                pdf.setFillColor(250, 250, 250);
              } else {
                pdf.setFillColor(245, 245, 245);
              }
              pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight, 'F');
              
              pdf.setDrawColor(200, 200, 200);
              pdf.setLineWidth(0.003);
              pdf.rect(tableX, tableRowY, totalTableWidth, cutTableRowHeight);
              
              pdf.setFontSize(8);
              pdf.text((cutIdx + 1).toString(), tableX + cutTableColWidths[0] / 2, tableRowY + cutTableRowHeight * 0.6 + 0.02, { align: 'center' });
              pdf.text(cutLabel, tableX + cutTableColWidths[0] + 0.2, tableRowY + cutTableRowHeight * 0.6 + 0.02);
              pdf.text(resultLabel, tableX + cutTableColWidths[0] + cutTableColWidths[1] + 0.3, tableRowY + cutTableRowHeight * 0.6 + 0.02);
              
              tableRowY += cutTableRowHeight;
            }
          }
        }
      }
    }
    
    // Add watermark for free users
    if (userSubscription?.tier !== 'premium') {
      try {
        // Import the watermark image
        const watermarkModule = await import('../assets/cut-creator-watermark.png');
        const watermarkPath = watermarkModule.default;
        
        // Load watermark image
        const watermarkImg = new Image();
        watermarkImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          watermarkImg.onload = resolve;
          watermarkImg.onerror = (err) => {
            console.error('Failed to load watermark:', err);
            reject(err);
          };
          watermarkImg.src = watermarkPath;
        });
        
        console.log('✅ Watermark loaded successfully');
        
        // Create canvas to apply opacity
        const watermarkCanvas = document.createElement('canvas');
        const watermarkCtx = watermarkCanvas.getContext('2d');
        watermarkCanvas.width = watermarkImg.width;
        watermarkCanvas.height = watermarkImg.height;
        
        // Draw with opacity
        watermarkCtx.globalAlpha = 0.1;
        watermarkCtx.drawImage(watermarkImg, 0, 0);
        
        const watermarkDataUrl = watermarkCanvas.toDataURL('image/png');
        
        // Add watermark to each page (semi-transparent, centered)
        const totalPages = pdf.internal.getNumberOfPages();
        const watermarkWidth = 5; // inches
        const watermarkHeight = (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const watermarkX = (pageWidth - watermarkWidth) / 2;
        const watermarkY = (pageHeight - watermarkHeight) / 2;
        
        console.log(`Adding watermark to ${totalPages} pages...`);
        
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.addImage(watermarkDataUrl, 'PNG', watermarkX, watermarkY, watermarkWidth, watermarkHeight);
        }
        
        console.log('✅ Watermark added to all pages');
      } catch (error) {
        console.warn('Could not add watermark to PDF:', error);
        // Continue without watermark if image fails to load
      }
    }
    
    // Save the PDF
    const filename = `${title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    console.error('Error details:', error.message, error.stack);
    alert(`Error generating PDF: ${error.message}\n\nPlease check the console for more details.`);
  }
};

export const handleExportSVG = async (optimizationResults, projectName, userSubscription, setUpgradeModalOpen) => {
  // Check if premium
  if (userSubscription?.tier !== 'premium') {
    alert('SVG export is a Premium feature. Upgrade to access CNC-ready file exports!');
    setUpgradeModalOpen(true);
    return;
  }
  
  if (!optimizationResults || !optimizationResults.stock_sheets) {
    alert('Please run optimization first before exporting SVG.');
    return;
  }
  
  try {
    // Filter out empty stock sheets
    const validLayouts = optimizationResults.stock_sheets.filter(stockSheet => stockSheet.panels && stockSheet.panels.length > 0);
    
    if (validLayouts.length === 0) {
      alert('No layouts to export. Please run optimization first.');
      return;
    }
    
    const projectFileName = (projectName || 'cutlist').replace(/[^a-z0-9]/gi, '_');
    
    // If only one stock sheet, download directly without ZIP
    if (validLayouts.length === 1) {
      const stockSheet = validLayouts[0];
      const sheetSize = stockSheet.statistics?.sheet_size || {};
      const width = sheetSize.width || 48;
      const height = sheetSize.length || 96;
      
      // Use 96 DPI (standard screen DPI) - 96 pixels = 1 inch
      const widthPx = width * 96;
      const heightPx = height * 96;
      
      // Create SVG content with proper DPI
      let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${widthPx}px" height="${heightPx}px" 
     viewBox="0 0 ${widthPx} ${heightPx}" 
     xmlns="http://www.w3.org/2000/svg">
`;
      
      // Add each panel as a filled rectangle (96 pixels per inch)
      stockSheet.panels.forEach((panel) => {
        const x = (panel.x_position || 0) * 96;
        const y = (panel.y_position || 0) * 96;
        const w = panel.width * 96;
        const h = (panel.height || panel.length) * 96;
        
        svgContent += `  <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="black"/>\n`;
      });
      
      svgContent += `</svg>`;
      
      // Download single SVG file
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectFileName}.svg`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // Multiple layouts - create ZIP file
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      validLayouts.forEach((stockSheet, index) => {
        const sheetSize = stockSheet.statistics?.sheet_size || {};
        const width = sheetSize.width || 48;
        const height = sheetSize.length || 96;
        
        // Use 96 DPI (standard screen DPI) - 96 pixels = 1 inch
        const widthPx = width * 96;
        const heightPx = height * 96;
        
        // Create SVG content with proper DPI
        let svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${widthPx}px" height="${heightPx}px" 
     viewBox="0 0 ${widthPx} ${heightPx}" 
     xmlns="http://www.w3.org/2000/svg">
`;
        
        // Add each panel as a filled rectangle (96 pixels per inch)
        stockSheet.panels.forEach((panel) => {
          const x = (panel.x_position || 0) * 96;
          const y = (panel.y_position || 0) * 96;
          const w = panel.width * 96;
          const h = (panel.height || panel.length) * 96;
          
          svgContent += `  <rect x="${x}" y="${y}" width="${w}" height="${h}" fill="black"/>\n`;
        });
        
        svgContent += `</svg>`;
        
        // Add to ZIP
        zip.file(`sheet${index + 1}.svg`, svgContent);
      });
      
      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectFileName}_svg.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error exporting SVG:', error);
    alert('Error exporting SVG: ' + error.message);
  }
};

export const handleExportDXF = async (optimizationResults, projectName, userSubscription, setUpgradeModalOpen) => {
  // Check if premium
  if (userSubscription?.tier !== 'premium') {
    alert('DXF export is a Premium feature. Upgrade to access CAD-ready file exports!');
    setUpgradeModalOpen(true);
    return;
  }
  
  if (!optimizationResults || !optimizationResults.stock_sheets) {
    alert('Please run optimization first before exporting DXF.');
    return;
  }
  
  try {
    // Filter out empty stock sheets
    const validLayouts = optimizationResults.stock_sheets.filter(stockSheet => stockSheet.panels && stockSheet.panels.length > 0);
    
    if (validLayouts.length === 0) {
      alert('No layouts to export. Please run optimization first.');
      return;
    }
    
    const projectFileName = (projectName || 'cutlist').replace(/[^a-z0-9]/gi, '_');
    
    // If only one stock sheet, download directly without ZIP
    if (validLayouts.length === 1) {
      const stockSheet = validLayouts[0];
      
      // Create minimal DXF R12 format (most compatible)
      let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1009
9
$INSUNITS
70
1
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LTYPE
70
1
0
LTYPE
2
CONTINUOUS
70
0
3
Solid line
72
65
73
0
40
0.0
0
ENDTAB
0
TABLE
2
LAYER
70
1
0
LAYER
2
0
70
0
62
7
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;
      
      // Add each panel as four separate LINE entities
      stockSheet.panels.forEach((panel, panelIdx) => {
        const x = panel.x_position || 0;
        const y = panel.y_position || 0;
        const w = panel.width;
        const h = panel.length;
        
        // Bottom edge
        dxfContent += `0
LINE
8
0
10
${x}
20
${y}
30
0.0
11
${x + w}
21
${y}
31
0.0
`;
        // Right edge
        dxfContent += `0
LINE
8
0
10
${x + w}
20
${y}
30
0.0
11
${x + w}
21
${y + h}
31
0.0
`;
        // Top edge
        dxfContent += `0
LINE
8
0
10
${x + w}
20
${y + h}
30
0.0
11
${x}
21
${y + h}
31
0.0
`;
        // Left edge
        dxfContent += `0
LINE
8
0
10
${x}
20
${y + h}
30
0.0
11
${x}
21
${y}
31
0.0
`;
      });
      
      dxfContent += `0
ENDSEC
0
EOF`;
      
      // Download single DXF file
      const blob = new Blob([dxfContent], { type: 'application/dxf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectFileName}.dxf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      // Multiple layouts - create ZIP file
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      validLayouts.forEach((stockSheet, index) => {
        // Create minimal DXF R12 format (most compatible)
        let dxfContent = `0
SECTION
2
HEADER
9
$ACADVER
1
AC1009
9
$INSUNITS
70
1
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LTYPE
70
1
0
LTYPE
2
CONTINUOUS
70
0
3
Solid line
72
65
73
0
40
0.0
0
ENDTAB
0
TABLE
2
LAYER
70
1
0
LAYER
2
0
70
0
62
7
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
`;
        
        // Add each panel as four separate LINE entities
        stockSheet.panels.forEach((panel, panelIdx) => {
          const x = panel.x_position || 0;
          const y = panel.y_position || 0;
          const w = panel.width;
          const h = panel.length;
          
          // Bottom edge
          dxfContent += `0
LINE
8
0
10
${x}
20
${y}
30
0.0
11
${x + w}
21
${y}
31
0.0
`;
          // Right edge
          dxfContent += `0
LINE
8
0
10
${x + w}
20
${y}
30
0.0
11
${x + w}
21
${y + h}
31
0.0
`;
          // Top edge
          dxfContent += `0
LINE
8
0
10
${x + w}
20
${y + h}
30
0.0
11
${x}
21
${y + h}
31
0.0
`;
          // Left edge
          dxfContent += `0
LINE
8
0
10
${x}
20
${y + h}
30
0.0
11
${x}
21
${y}
31
0.0
`;
        });
        
        dxfContent += `0
ENDSEC
0
EOF`;
        
        // Add to ZIP
        zip.file(`sheet${index + 1}.dxf`, dxfContent);
      });
      
      // Generate and download ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectFileName}_dxf.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error exporting DXF:', error);
    alert('Error exporting DXF: ' + error.message);
  }
};
